import React from 'react';
import { Shield, Lock, Eye, Database, Globe, Bell } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20 relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-pattern animate-gradient"></div>
      <div className="gradient-orb gradient-orb-1"></div>
      <div className="gradient-orb gradient-orb-2"></div>
      <div className="gradient-orb gradient-orb-3"></div>
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600">
              Policy
            </span>
          </h1>
          <p className="text-xl text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
        
        <div className="space-y-8">
          <PrivacySection
            icon={<Shield className="h-6 w-6 text-purple-400" />}
            title="1. Information We Collect"
            content={
              <div className="space-y-4">
                <p>Distrotopia Ltd collects information that you provide directly to us, including:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Account information (name, email, password)</li>
                  <li>Artist/band profile information</li>
                  <li>Payment and banking details</li>
                  <li>Music content and metadata</li>
                  <li>Communication preferences</li>
                </ul>
              </div>
            }
          />

          <PrivacySection
            icon={<Lock className="h-6 w-6 text-purple-400" />}
            title="2. How We Use Your Information"
            content="We use the collected information to provide our distribution services, process payments, communicate with you about your account, and improve our services. Your music content is used solely for distribution to your selected platforms."
          />

          <PrivacySection
            icon={<Eye className="h-6 w-6 text-purple-400" />}
            title="3. Information Sharing"
            content="We share your information with streaming platforms and stores as necessary for distribution. We never sell your personal data to third parties. We may share anonymous, aggregated data for analytics purposes."
          />

          <PrivacySection
            icon={<Database className="h-6 w-6 text-purple-400" />}
            title="4. Data Security"
            content="We implement industry-standard security measures to protect your personal information. This includes encryption, secure servers, and regular security audits. We regularly review and update our security practices."
          />

          <PrivacySection
            icon={<Globe className="h-6 w-6 text-purple-400" />}
            title="5. International Data Transfers"
            content="Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for these transfers through standard contractual clauses and other legal mechanisms."
          />

          <PrivacySection
            icon={<Bell className="h-6 w-6 text-purple-400" />}
            title="6. Your Rights"
            content={
              <div className="space-y-4">
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to data processing</li>
                  <li>Receive your data in a portable format</li>
                </ul>
              </div>
            }
          />
        </div>

        <div className="mt-12 p-6 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <p className="text-center text-gray-400 text-sm">
            For privacy-related inquiries, please contact our Data Protection Officer at{' '}
            <a href="mailto:privacy@distrotopia.com" className="text-purple-400 hover:text-purple-300 transition-colors">
              privacy@distrotopia.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const PrivacySection = ({ 
  icon, 
  title, 
  content 
}: { 
  icon: React.ReactNode, 
  title: string, 
  content: React.ReactNode 
}) => {
  return (
    <div className="bg-gray-900/50 rounded-lg p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all">
      <div className="flex items-center space-x-3 mb-4">
        <div className="bg-purple-500/10 p-2 rounded-lg">
          {icon}
        </div>
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      <div className="text-gray-300 leading-relaxed">
        {content}
      </div>
    </div>
  );
};

export default Privacy;